import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../../components/layouts/DashboardLayout';
import { getDate, handleClick } from '../../../components/utils/helper';
import { getCustomerSchema, getTransactionSchema } from '../../../components/actions/schema';
import { getCampaignbyID, updateCamapign, uploadImage } from '../../../components/actions/campaign';
import Loader from '../../../components/utils/Loader';

const DEFAULT_IMAGE_URL = 'https://storage.primexloyalty.com/general_20260801083525385_IMG.png';
const generateId = () => Math.random().toString(36).substr(2, 9);

const formatDateForInput = (dateString) => {
  if (!dateString) return getDate(new Date(), "YYYY-MM-DD");
  return dateString.split('T')[0]; 
};

const isRuleSetFilled = (ruleSet) => {
  const hasValidRules = ruleSet.rules?.some(
    (rule) => rule.field && rule.operator && rule.value
  );
  const hasValidGroups = ruleSet.groups?.some(
    (group) => group.rules?.some(
      (rule) => rule.field && rule.operator && rule.value
    )
  );
  return hasValidRules || hasValidGroups;
};

// Enhanced parseRuleString to handle different operators
const parseRuleString = (ruleString, fields, defaultCondition = 'AND') => {
  if (!ruleString || ruleString.trim() === '') {
    return {
      condition: defaultCondition,
      rules: [{ field: '', operator: '', value: '', value2: '' }],
      groups: [],
    };
  }

  const cleanedRule = ruleString.replace(/^\(|\)$/g, '');
  const condition = cleanedRule.includes(' and ') ? 'AND' : cleanedRule.includes(' or ') ? 'OR' : defaultCondition;
  const ruleParts = cleanedRule.split(` ${condition.toLowerCase()} `);

  const parseSingleRule = (ruleStr) => {
    // Handle BETWEEN operators
    if (ruleStr.includes(' BETWEEN ')) {
      const [field, values] = ruleStr.split(' BETWEEN ');
      const [value1, value2] = values.split(' AND ').map(v => v.trim().replace(/^'|'$/g, ''));
      return {
        field: field.trim(),
        operator: 'between',
        value: value1,
        value2: value2
      };
    }
    
    // Handle NOT BETWEEN operators
    if (ruleStr.includes(' NOT BETWEEN ')) {
      const [field, values] = ruleStr.split(' NOT BETWEEN ');
      const [value1, value2] = values.split(' AND ').map(v => v.trim().replace(/^'|'$/g, ''));
      return {
        field: field.trim(),
        operator: 'not_between',
        value: value1,
        value2: value2
      };
    }
    
    // Handle LIKE operators (contains)
    if (ruleStr.includes(' LIKE ')) {
      const [field, value] = ruleStr.split(' LIKE ');
      const cleanedValue = value.replace(/^'%|%'$/g, '');
      return {
        field: field.trim(),
        operator: 'contains',
        value: cleanedValue
      };
    }
    
    // Handle NOT LIKE operators
    if (ruleStr.includes(' NOT LIKE ')) {
      const [field, value] = ruleStr.split(' NOT LIKE ');
      const cleanedValue = value.replace(/^'%|%'$/g, '');
      return {
        field: field.trim(),
        operator: 'not_contains',
        value: cleanedValue
      };
    }
    
    // Handle other operators
    const operatorMap = {
      '!=': 'not_equals',
      '>=': 'greater_than_equal',
      '<=': 'less_than_equal',
      '>': 'greater_than',
      '<': 'less_than',
      '=': 'equals'
    };

    for (const [opSymbol, opValue] of Object.entries(operatorMap)) {
      if (ruleStr.includes(` ${opSymbol} `)) {
        const [field, value] = ruleStr.split(` ${opSymbol} `);
        const cleanedValue = value.replace(/^'|'$/g, '');
        return {
          field: field.trim(),
          operator: opValue,
          value: cleanedValue
        };
      }
    }

    // Default to equals
    const [field, value] = ruleStr.split(' = ').map(s => s.trim());
    const cleanedValue = value?.replace(/^'|'$/g, '') || '';
    return {
      field,
      operator: 'equals',
      value: cleanedValue
    };
  };

  const rules = ruleParts
    .map((part) => {
      if (part.startsWith('(') && part.endsWith(')')) {
        const groupContent = part.replace(/^\(|\)$/g, '');
        const groupCondition = groupContent.includes(' or ') ? 'OR' : 'AND';
        const groupRuleParts = groupContent.split(` ${groupCondition.toLowerCase()} `);
        
        const groupRules = groupRuleParts.map(parseSingleRule);
        
        return {
          id: generateId(),
          condition: groupCondition,
          rules: groupRules,
        };
      }
      return parseSingleRule(part);
    })
    .filter((rule) => rule.field || rule.id);

  return {
    condition,
    rules: rules.filter((rule) => !rule.id),
    groups: rules.filter((rule) => rule.id),
  };
};

export default function EditCampaign() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams(); 
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showImageSuccessModal, setShowImageSuccessModal] = useState(false);
  const [customerData, setCustomerData] = useState([]);
  const [transactionData, setTransactionData] = useState([]);
  const [error, setError] = useState(null);
  const nextSixMonth = handleClick();
  const code = sessionStorage.getItem("code");
   const [imageUploading, setImageUploading] = useState(false);

  const [formData, setFormData] = useState({
    rule_code: "",
    create_date: new Date().toISOString(),
    updateDate: new Date().toISOString(),
    cVersion: "",
    clientid: sessionStorage.getItem("code"),
    rule_name: "Point Campaign",
    rule_description: "New campaign to grow my organization",
    rule_for_current_request: "",
    start_date: getDate(new Date(), "YYYY-MM-DD"),
    end_date: nextSixMonth,
    rule_on_existing_data: "",
    group_by_rule_on_existing_data: "",
    status: "1",
    rewardratio: 25,
    cust_qualify_rule: "",
    earningtype: "Fix",
    earningvalue: 100,
    frequency_type_existing_data: "",
    frequency_value_existing_data: 0,
    frequency_start_date_existing_data: getDate(new Date(), "YYYY-MM-DD"),
    frequency_end_date_existing_data: getDate(new Date(), "YYYY-MM-DD"),
    transaction_field: "",
    every_occurrence: "",
    campaignImage: '',
    tier_rule: "",
    membership_rule: "",
    imagePreview: '',
    customerRules: [],
    transactionRules: [],
    campaignImageUrl: "",
    referral_rule_aply: "",
    referral_who_earn: "",
    earning_limit_campaign: 0,
    campaign_spent_limit: 0,
  });

  const [customerRule, setCustomerRule] = useState({
    condition: 'AND',
    rules: [{ field: '', operator: '', value: '', value2: '' }],
  });

  const [transactionRules, setTransactionRules] = useState([
    {
      id: generateId(),
      name: 'Transaction Rule 1',
      incomingRule: {
        condition: 'AND',
        rules: [{ field: '', operator: '', value: '', value2: '' }],
        groups: [],
      },
      functionExistingRule: {
        condition: 'AND',
        rules: [{ field: '', operator: '', value: '', value2: '' }],
        groups: [],
      },
      functionDuration: '',
      functionValue: '0',
      frequencyStartDate: getDate(new Date(), "YYYY-MM-DD"),
      frequencyEndDate: getDate(new Date(), "YYYY-MM-DD"),
      functionAdditionalRule: {
        condition: 'AND',
        rules: [{ field: '', operator: '', value: '', value2: '' }],
        groups: [],
      },
      rewardConfig: {
        earningType: 'Fix',
        rewardratio: 25,
        earningValue: '',
      },
    },
  ]);

  const [showAdvancedRules, setShowAdvancedRules] = useState({});

  // Get operators based on field type
  const getOperatorsForField = (fieldType, valueType) => {
    const baseOperators = [
      { value: 'equals', label: 'Equals', requiresSecondValue: false },
      { value: 'not_equals', label: 'Not Equals', requiresSecondValue: false },
    ];

    const numberOperators = [
      { value: 'greater_than', label: 'Greater Than', requiresSecondValue: false },
      { value: 'greater_than_equal', label: 'Greater Than or Equal To', requiresSecondValue: false },
      { value: 'less_than', label: 'Less Than', requiresSecondValue: false },
      { value: 'less_than_equal', label: 'Less Than or Equal To', requiresSecondValue: false },
      { value: 'between', label: 'Between', requiresSecondValue: true },
      { value: 'not_between', label: 'Not Between', requiresSecondValue: true },
    ];

    const stringOperators = [
     { value: 'greater_than', label: 'Greater Than', requiresSecondValue: false },
      { value: 'greater_than_equal', label: 'Greater Than or Equal To', requiresSecondValue: false },
      { value: 'less_than', label: 'Less Than', requiresSecondValue: false },
      { value: 'less_than_equal', label: 'Less Than or Equal To', requiresSecondValue: false },
      { value: 'between', label: 'Between', requiresSecondValue: true },
      { value: 'not_between', label: 'Not Between', requiresSecondValue: true },
      { value: 'in', label: 'In', requiresSecondValue: false },
      { value: 'not_in', label: 'Not In', requiresSecondValue: false },
    ];

    let operators = [...baseOperators];

    // Add number operators for numeric fields
    if (valueType === 'number' || fieldType === 'integer' || fieldType === 'float') {
      operators = [...operators, ...numberOperators];
    }

    // Add string operators for text fields
    if (valueType === 'text' || fieldType === 'string') {
      operators = [...operators, ...stringOperators];
    }

    return operators;
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      let customerList = [];
let transactionList = [];
let campaignData = {};

try {
  const customerRes = await getCustomerSchema(code);
  customerList = customerRes?.data || [];
} catch (err) {
  console.error("Customer Schema Error", err);
}

try {
  const transactionRes = await getTransactionSchema(code);
  transactionList = transactionRes?.data || [];
} catch (err) {
  console.error("Transaction Schema Error", err);
}

try {
  const campaignRes = await getCampaignbyID(id);
  campaignData = campaignRes?.data || {};
} catch (err) {
  console.error("Campaign Error", err);
}

setCustomerData(customerList);
setTransactionData(transactionList);
      
      const formattedStartDate = formatDateForInput(campaignData.start_date);
      const formattedEndDate = formatDateForInput(campaignData.end_date);
      const formattedFrequencyStartDate = formatDateForInput(campaignData.frequency_start_date_existing_data);
      const formattedFrequencyEndDate = formatDateForInput(campaignData.frequency_end_date_existing_data);
      
      const parsedCustomerRule = parseRuleString(campaignData.cust_qualify_rule, customerList.map(item => ({
        name: item.fieldname || '',
        label: item.jsonfield || item.fieldname || '',
        valueType: item.fieldtype === "integer" || item.fieldtype === "float" ? "number" : "text",
        fieldtype: item.fieldtype || '',
      })));

      const parsedIncomingRule = parseRuleString(campaignData.rule_for_current_request, transactionList.map(item => ({
        name: item.fieldname || '',
        label: item.jsonfield || item.fieldname || '',
        valueType: item.DataType === "integer" || item.DataType === "float" ? "number" : "text",
        DataType: item.DataType || '',
      })), 'OR');

      const parsedExistingRule = parseRuleString(campaignData.rule_on_existing_data, transactionList.map(item => ({
        name: item.fieldname || '',
        label: item.jsonfield || item.fieldname || '',
        valueType: item.DataType === "integer" || item.DataType === "float" ? "number" : "text",
        DataType: item.DataType || '',
      })));

      const parsedAdditionalRule = parseRuleString(campaignData.group_by_rule_on_existing_data, [
        ...transactionList
          .filter((item) => ["integer", "float"].includes(item.DataType))
          .map((item) => ({
            name: item.jsonfield || item.fieldname || '',
            label: item.jsonfield || item.fieldname || '',
            valueType: "number",
            DataType: item.DataType || '',
          })),
        {
          name: "transactionCount",
          label: "transactionCount",
          valueType: "number",
          DataType: "integer",
        },
      ]);
      
      const newTransactionRules = [{
        id: generateId(),
        name: 'Transaction Rule 1',
        incomingRule: parsedIncomingRule,
        functionExistingRule: parsedExistingRule,
        functionDuration: campaignData.frequency_type_existing_data || '',
        functionValue: String(campaignData.frequency_value_existing_data || '0'),
        frequencyStartDate: formattedFrequencyStartDate,
        frequencyEndDate: formattedFrequencyEndDate,
        functionAdditionalRule: parsedAdditionalRule,
        rewardConfig: {
          earningType: campaignData.earningtype || 'Fix',
          rewardratio: campaignData.rewardratio || 25,
          earningValue: String(campaignData.earningvalue || ''),
        },
      }];
      
      const initialShowAdvancedRules = {};
      newTransactionRules.forEach((rule) => {
        initialShowAdvancedRules[rule.id] = isRuleSetFilled(rule.functionExistingRule) || isRuleSetFilled(rule.functionAdditionalRule);
      });
      
      setFormData({
        ...formData,
        rule_code: campaignData.rule_code || "",
        create_date: campaignData.create_date || new Date().toISOString(),
        updateDate: new Date().toISOString(),
        cVersion: campaignData.cVersion || "",
        clientid: campaignData.clientid || sessionStorage.getItem("code"),
        rule_name: campaignData.rule_name || "Point Campaign",
        rule_description: campaignData.rule_description || "New campaign to grow my organization",
        rule_for_current_request: campaignData.rule_for_current_request || "",
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        rule_on_existing_data: campaignData.rule_on_existing_data || "",
        group_by_rule_on_existing_data: campaignData.group_by_rule_on_existing_data || "",
        status: campaignData.status || "1",
        rewardratio: campaignData.rewardratio || 25,
        cust_qualify_rule: campaignData.cust_qualify_rule || "",
        earningtype: campaignData.earningtype || "Fix",
        earningvalue: campaignData.earningvalue || 100,
        frequency_type_existing_data: campaignData.frequency_type_existing_data || "",
        frequency_value_existing_data: campaignData.frequency_value_existing_data || 0,
        frequency_start_date_existing_data: formattedFrequencyStartDate,
        frequency_end_date_existing_data: formattedFrequencyEndDate,
        transaction_field: campaignData.transaction_field || "",
        every_occurrence: campaignData.every_occurrence || "",
        campaignImage: campaignData.campaignImage || '',
        tier_rule: campaignData.tier_rule || "",
        membership_rule: campaignData.membership_rule || "",
        imagePreview: campaignData.campaignImageUrl || '',
        customerRules: [],
        transactionRules: [],
        campaignImageUrl: campaignData.campaignImageUrl || "",
        referral_rule_aply: campaignData.referral_rule_aply || "",
        referral_who_earn: campaignData.referral_who_earn || "",
        earning_limit_campaign: campaignData.earning_limit_campaign || 0,
        campaign_spent_limit: campaignData.campaign_spent_limit || 0,
      });

      setCustomerRule(parsedCustomerRule);
      setTransactionRules(newTransactionRules);
      setShowAdvancedRules(initialShowAdvancedRules);

      setLoading(false);
   } catch (err) {
  console.error(err);
} finally {
  setLoading(false);
}
  };

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  const customerFields = useMemo(() => {
    const fields = customerData.map((item) => ({
      name: item.fieldname || '',
      label: item.jsonfield || item.fieldname || '',
      valueType: item.fieldtype === "integer" || item.fieldtype === "float" ? "number" : "text",
      fieldtype: item.fieldtype || '',
    }));
    return fields;
  }, [customerData]);

  const transactionFields = useMemo(() => {
    const fields = transactionData.map((item) => ({
      name: item.fieldname || '',
      label: item.jsonfield || item.fieldname || '',
      valueType: item.DataType === "integer" || item.DataType === "float" ? "number" : "text",
      DataType: item.DataType || '',
    }));
    return fields;
  }, [transactionData]);

  const functionTransactionFields = useMemo(() => {
    const filteredFields = transactionData
      .filter((item) => ["integer", "float"].includes(item.DataType))
      .map((item) => ({
        name: item.jsonfield || item.fieldname || '',
        label: item.jsonfield || item.fieldname || '',
        valueType: "number",
        DataType: item.DataType || '',
      }));
    filteredFields.push({
      name: "transactionCount",
      label: "transactionCount",
      valueType: "number",
      DataType: "integer",
    });
    return filteredFields;
  }, [transactionData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("Image size should be less than 5MB");
      return;
    }

    setError("");

    // 🔥 IMPORTANT: loader ko next tick me on karo
    setTimeout(async () => {
      setImageUploading(true);

      // preview
      const reader = new FileReader();
      reader.onload = () => {
        setFormData((prev) => ({
          ...prev,
          imagePreview: reader.result,
        }));
      };
      reader.readAsDataURL(file);

      try {
        // 🔥 API hit (ab loader already visible hai)
        const response = await uploadImage(code, file);

        setFormData((prev) => ({
          ...prev,
          campaignImage: file,
          campaignImageUrl: response.url,
        }));
      } catch (err) {
        setError("Image upload failed");
      } finally {
        setImageUploading(false);
      }
    }, 0); // 👈 MAGIC LINE
  };

  const removeImage = () => {
    setFormData((prev) => ({
      ...prev,
      campaignImage: '',
      imagePreview: '',
      campaignImageUrl: '',
    }));
    setError(null);
  };

  const handleImageSubmit = async () => {
    if (!formData.campaignImage) {
      setError('No image selected for upload');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await uploadImage(code, formData.campaignImage);
      if (response && response.url) {
        setFormData((prev) => ({
          ...prev,
          campaignImageUrl: response.url,
        }));
        setShowImageSuccessModal(true);
      } else {
        setError('Failed to upload image. Please try again.');
      }
    } catch (err) {
      setError('Error uploading image. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSuccessClose = () => {
    setShowImageSuccessModal(false);
  };

  const addTransactionRule = () => {
    const newRule = {
      id: generateId(),
      name: `Transaction Rule ${transactionRules.length + 1}`,
      incomingRule: {
        condition: 'AND',
        rules: [{ field: '', operator: '', value: '', value2: '' }],
        groups: [],
      },
      functionExistingRule: {
        condition: 'AND',
        rules: [{ field: '', operator: '', value: '', value2: '' }],
        groups: [],
      },
      functionDuration: '',
      functionValue: '0',
      frequencyStartDate: getDate(new Date(), "YYYY-MM-DD"),
      frequencyEndDate: getDate(new Date(), "YYYY-MM-DD"),
      functionAdditionalRule: {
        condition: 'AND',
        rules: [{ field: '', operator: '', value: '', value2: '' }],
        groups: [],
      },
      rewardConfig: {
        earningType: 'Fix',
        rewardratio: 25,
        earningValue: '',
      },
    };
    setTransactionRules((prev) => [...prev, newRule]);
    setShowAdvancedRules((prev) => ({ ...prev, [newRule.id]: false }));
  };

  const removeTransactionRule = (ruleId) => {
    if (transactionRules.length > 1) {
      setTransactionRules((prev) => prev.filter((rule) => rule.id !== ruleId));
      setShowAdvancedRules((prev) => {
        const newState = { ...prev };
        delete newState[ruleId];
        return newState;
      });
    }
  };

  const updateTransactionRuleName = (ruleId, name) => {
    setTransactionRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId ? { ...rule, name } : rule
      )
    );
  };

  const updateTransactionRuleRewardConfig = (ruleId, field, value) => {
    setTransactionRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? { ...rule, rewardConfig: { ...rule.rewardConfig, [field]: value } }
          : rule
      )
    );
  };

  const addRule = () => {
    setCustomerRule((prev) => ({
      ...prev,
      rules: [...prev.rules, { field: '', operator: '', value: '', value2: '' }],
    }));
  };
const removeRule = (index) => {
  setCustomerRule((prev) => {
    if (prev.rules.length === 1) {
      return {
        ...prev,
        rules: prev.rules.map((rule, i) =>
          i === index
            ? { field: '', operator: '', value: '', value2: '' }
            : rule
        ),
      };
    }
    return {
      ...prev,
      rules: prev.rules.filter((_, i) => i !== index),
    };
  });
};
  const updateRule = (index, field, value) => {
    setCustomerRule((prev) => ({
      ...prev,
      rules: prev.rules.map((rule, i) =>
        i === index ? { ...rule, [field]: value } : rule
      ),
    }));
  };

  const addTransactionRuleToSection = (ruleId, section) => {
    setTransactionRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? {
            ...rule,
            [section]: {
              ...rule[section],
              rules: [
                ...rule[section].rules,
                { field: '', operator: '', value: '', value2: '' },
              ],
            },
          }
          : rule
      )
    );
  };
const removeTransactionRuleFromSection = (ruleId, section, index) => {
  setTransactionRules((prev) =>
    prev.map((rule) => {
      if (rule.id !== ruleId) return rule;
      const sectionRules = rule[section].rules;

      if (sectionRules.length === 1) {
        return {
          ...rule,
          [section]: {
            ...rule[section],
            rules: sectionRules.map((r, i) =>
              i === index
                ? { field: '', operator: '', value: '', value2: '' }
                : r
            ),
          },
        };
      }

      return {
        ...rule,
        [section]: {
          ...rule[section],
          rules: sectionRules.filter((_, i) => i !== index),
        },
      };
    })
  );
};

  const updateTransactionRuleInSection = (ruleId, section, index, field, value) => {
    setTransactionRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? {
            ...rule,
            [section]: {
              ...rule[section],
              rules: rule[section].rules.map((r, i) =>
                i === index ? { ...r, [field]: value } : r
              ),
            },
          }
          : rule
      )
    );
  };

  const updateTransactionRuleCondition = (ruleId, section, condition) => {
    setTransactionRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? {
            ...rule,
            [section]: {
              ...rule[section],
              condition,
            },
          }
          : rule
      )
    );
  };

  const addGroup = (ruleId, section) => {
    const newGroup = {
      id: generateId(),
      condition: 'AND',
      rules: [{ field: '', operator: '', value: '', value2: '' }],
    };

    setTransactionRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? {
            ...rule,
            [section]: {
              ...rule[section],
              groups: [...(rule[section].groups || []), newGroup],
            },
          }
          : rule
      )
    );
  };

  const removeGroup = (ruleId, section, groupId) => {
    setTransactionRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? {
            ...rule,
            [section]: {
              ...rule[section],
              groups: (rule[section].groups || []).filter(
                (group) => group.id !== groupId
              ),
            },
          }
          : rule
      )
    );
  };

  const updateGroupCondition = (ruleId, section, groupId, condition) => {
    setTransactionRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? {
            ...rule,
            [section]: {
              ...rule[section],
              groups: (rule[section].groups || []).map((group) =>
                group.id === groupId ? { ...group, condition } : group
              ),
            },
          }
          : rule
      )
    );
  };

  const addRuleToGroup = (ruleId, section, groupId) => {
    setTransactionRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? {
            ...rule,
            [section]: {
              ...rule[section],
              groups: (rule[section].groups || []).map((group) =>
                group.id === groupId
                  ? {
                    ...group,
                    rules: [
                      ...group.rules,
                      { field: '', operator: '', value: '', value2: '' },
                    ],
                  }
                  : group
              ),
            },
          }
          : rule
      )
    );
  };
const removeRuleFromGroup = (ruleId, section, groupId, ruleIndex) => {
  setTransactionRules((prev) =>
    prev.map((rule) =>
      rule.id === ruleId
        ? {
          ...rule,
          [section]: {
            ...rule[section],
            groups: (rule[section].groups || []).map((group) =>
              group.id === groupId
                ? {
                  ...group,
                  rules:
                    group.rules.length === 1
                      ? group.rules.map((r, i) =>
                          i === ruleIndex
                            ? { field: '', operator: '', value: '', value2: '' }
                            : r
                        )
                      : group.rules.filter((_, i) => i !== ruleIndex),
                }
                : group
            ),
          },
        }
        : rule
    )
  );
};

  const updateGroupRule = (ruleId, section, groupId, ruleIndex, field, value) => {
    setTransactionRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? {
            ...rule,
            [section]: {
              ...rule[section],
              groups: (rule[section].groups || []).map((group) =>
                group.id === groupId
                  ? {
                    ...group,
                    rules: group.rules.map((r, i) =>
                      i === ruleIndex ? { ...r, [field]: value } : r
                    ),
                  }
                  : group
              ),
            },
          }
          : rule
      )
    );
  };

  const getOperatorSymbol = (operator) => {
    switch (operator) {
      case 'equals': return '=';
      case 'not_equals': return '!=';
      case 'greater_than': return '>';
      case 'greater_than_equal': return '>=';
      case 'less_than': return '<';
      case 'less_than_equal': return '<=';
      case 'between': return 'BETWEEN';
      case 'not_between': return 'NOT BETWEEN';
      case 'contains': return 'LIKE';
      case 'not_contains': return 'NOT LIKE';
      case 'starts_with': return 'LIKE';
      case 'ends_with': return 'LIKE';
      default: return operator;
    }
  };

  const generateRuleString = (ruleSet, fields) => {
    const { rules, condition, groups = [] } = ruleSet;
    if (!rules || rules.length === 0) return '';

    const ruleStrings = rules
      .filter((rule) => rule.field && rule.operator && (rule.value || rule.value2))
      .map((rule) => {
        const field = fields.find((f) => f.name === rule.field);
        const operator = getOperatorSymbol(rule.operator);
        const valueType = field?.valueType || 'text';
        
        if (rule.operator === 'between' || rule.operator === 'not_between') {
          if (!rule.value || !rule.value2) return '';
          const value1 = valueType === 'number' ? rule.value : `'${rule.value}'`;
          const value2 = valueType === 'number' ? rule.value2 : `'${rule.value2}'`;
          return `${rule.field} ${operator} ${value1} AND ${value2}`;
        } else if (rule.operator === 'contains' || rule.operator === 'not_contains') {
          const value = valueType === 'number' ? rule.value : `'%${rule.value}%'`;
          return `${rule.field} ${operator} ${value}`;
        } else if (rule.operator === 'starts_with') {
          const value = valueType === 'number' ? rule.value : `'${rule.value}%'`;
          return `${rule.field} ${operator} ${value}`;
        } else if (rule.operator === 'ends_with') {
          const value = valueType === 'number' ? rule.value : `'%${rule.value}'`;
          return `${rule.field} ${operator} ${value}`;
        } else {
          const value = valueType === 'number' ? rule.value : `'${rule.value}'`;
          return `${rule.field} ${operator} ${value}`;
        }
      });

    const groupStrings = groups
      .map((group) => {
        const groupRules = group.rules
          .filter((rule) => rule.field && rule.operator && (rule.value || rule.value2))
          .map((rule) => {
            const field = fields.find((f) => f.name === rule.field);
            const operator = getOperatorSymbol(rule.operator);
            const valueType = field?.valueType || 'text';
            
            if (rule.operator === 'between' || rule.operator === 'not_between') {
              if (!rule.value || !rule.value2) return '';
              const value1 = valueType === 'number' ? rule.value : `'${rule.value}'`;
              const value2 = valueType === 'number' ? rule.value2 : `'${rule.value2}'`;
              return `${rule.field} ${operator} ${value1} AND ${value2}`;
            } else if (rule.operator === 'contains' || rule.operator === 'not_contains') {
              const value = valueType === 'number' ? rule.value : `'%${rule.value}%'`;
              return `${rule.field} ${operator} ${value}`;
            } else if (rule.operator === 'starts_with') {
              const value = valueType === 'number' ? rule.value : `'${rule.value}%'`;
              return `${rule.field} ${operator} ${value}`;
            } else if (rule.operator === 'ends_with') {
              const value = valueType === 'number' ? rule.value : `'%${rule.value}'`;
              return `${rule.field} ${operator} ${value}`;
            } else {
              const value = valueType === 'number' ? rule.value : `'${rule.value}'`;
              return `${rule.field} ${operator} ${value}`;
            }
          });

        if (groupRules.length === 0) return '';
        return `(${groupRules.join(` ${group.condition.toLowerCase()} `)})`;
      })
      .filter((str) => str !== '');

    const allRules = [...ruleStrings, ...groupStrings];
    if (allRules.length === 0) return '';
    return `(${allRules.join(` ${condition.toLowerCase()} `)})`;
  };

  const generateCustQualifyRule = () => {
    return generateRuleString(customerRule, customerFields);
  };

  const generateTransactionRuleStrings = () => {
    const ruleForCurrentRequest = transactionRules
      .map((rule) => generateRuleString(rule.incomingRule, transactionFields))
      .filter((str) => str !== '')
      .join('; ');

    const ruleOnExistingData = transactionRules
      .map((rule) => generateRuleString(rule.functionExistingRule, transactionFields))
      .filter((str) => str !== '')
      .join('; ');

    const groupByRuleOnExistingData = transactionRules
      .map((rule) => generateRuleString(rule.functionAdditionalRule, functionTransactionFields))
      .filter((str) => str !== '')
      .join('; ');

    return {
      rule_for_current_request: ruleForCurrentRequest || '',
      rule_on_existing_data: ruleOnExistingData || '',
      group_by_rule_on_existing_data: groupByRuleOnExistingData || '',
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!formData.rule_name || !formData.start_date || !formData.end_date) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }
    const transactionRuleStrings = generateTransactionRuleStrings();
    const fieldsToExclude = ['campaignImage', 'imagePreview', 'customerRules', 'transactionRules'];
    const firstTransactionRule = transactionRules[0];
    const updatedFormData = {
      ...formData,
      rule_code: id,
      updateDate: new Date().toISOString(),
      cust_qualify_rule: generateCustQualifyRule(),
      rule_for_current_request: transactionRuleStrings.rule_for_current_request,
      rule_on_existing_data: transactionRuleStrings.rule_on_existing_data,
      group_by_rule_on_existing_data: transactionRuleStrings.group_by_rule_on_existing_data,
      frequency_type_existing_data: firstTransactionRule.functionDuration,
      frequency_value_existing_data: firstTransactionRule.functionDuration === 'Custom' ? 0 : parseInt(firstTransactionRule.functionValue || '0', 10),
      frequency_start_date_existing_data: firstTransactionRule.functionDuration === 'Custom' ? firstTransactionRule.frequencyStartDate : getDate(new Date(), "YYYY-MM-DD"),
      frequency_end_date_existing_data: firstTransactionRule.functionDuration === 'Custom' ? firstTransactionRule.frequencyEndDate : getDate(new Date(), "YYYY-MM-DD"),
      campaignImageUrl: formData?.campaignImageUrl || DEFAULT_IMAGE_URL,
    };
    fieldsToExclude.forEach(field => {
      delete updatedFormData[field];
    });

    try {
      const res = await updateCamapign(updatedFormData);
      if (res.message === "success") {
        setShowSuccessModal(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Error updating campaign:', err);
      setError('Failed to update campaign. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate('/campaigns');
  };

  const earningTypes = [
    { value: 'Fix', label: 'Fixed Points' },
    { value: 'Percentage', label: 'Percentage Based' },
  ];

  const durationOptions = [
    { value: "OneTime", label: "One Time" },
    { value: "Daily", label: "Day" },
    { value: "Weekly", label: "Weekly" },
    { value: "Monthly", label: "Monthly" },
    { value: "Quarterly", label: "Quarterly" },
    { value: "Yearly", label: "Yearly" },
    { value: "Custom", label: "Custom" },
  ];

  const renderRuleBuilder = (
    ruleId,
    rules,
    condition,
    onAddRule,
    onRemoveRule,
    onUpdateRule,
    onConditionChange,
    section,
    fields
  ) => (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => onConditionChange('AND')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors cursor-pointer ${condition === 'AND'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            AND
          </button>
          <button
            type="button"
            onClick={() => onConditionChange('OR')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors cursor-pointer ${condition === 'OR'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            OR
          </button>
        </div>

        <button
          type="button"
          onClick={onAddRule}
          className="inline-flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer text-sm font-medium"
        >
          <i className="ri-add-line"></i>
          Rule
        </button>

        {section && (
          <button
            type="button"
            onClick={() => addGroup(ruleId, section)}
            className="inline-flex items-center gap-2 px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors cursor-pointer text-sm font-medium"
          >
            <i className="ri-add-line"></i>
            Group
          </button>
        )}
      </div>

      <div className="space-y-3">
        {rules.map((rule, index) => {
          const currentField = fields.find((f) => f.name === rule.field);
          const operators = getOperatorsForField(currentField?.fieldtype || currentField?.DataType, currentField?.valueType);
          const currentOperator = operators.find(op => op.value === rule.operator);
          const requiresSecondValue = currentOperator?.requiresSecondValue || false;

          return (
            <div
              key={index}
              className={`p-3 rounded-lg border ${requiresSecondValue ? 'bg-blue-50 border-blue-200' : 'bg-blue-50 border-blue-200'}`}
            >
              <div className={`grid gap-3 ${requiresSecondValue ? 'grid-cols-1 md:grid-cols-5' : 'grid-cols-1 md:grid-cols-4'}`}>
                <div>
             <select
  value={rule.field}
  onChange={(e) => {
    onUpdateRule(index, "field", e.target.value);
    onUpdateRule(index, "operator", "");
    onUpdateRule(index, "value", "");
    onUpdateRule(index, "value2", "");
  }}
  className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-xs bg-white"
>
  <option value="">Select Field</option>

  {fields.map((field) => (
    <option key={field.name} value={field.name}>
      {field.label}
    </option>
  ))}
</select>
                </div>

                <div>
                  <select
                    value={rule.operator}
                    onChange={(e) => onUpdateRule(index, 'operator', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                  >
                    <option value="">Select Operator</option>
                    {operators.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <input
                    type={currentField?.valueType || 'text'}
                    value={rule.value}
                    onChange={(e) => onUpdateRule(index, 'value', e.target.value)}
                    placeholder={requiresSecondValue ? "Start value" : "Enter value"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                  />
                </div>

                {requiresSecondValue && (
                  <div>
                    <input
                      type={currentField?.valueType || 'text'}
                      value={rule.value2 || ''}
                      onChange={(e) => onUpdateRule(index, 'value2', e.target.value)}
                      placeholder="End value"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                    />
                  </div>
                )}

                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => onRemoveRule(index)}
                    className="w-8 h-8 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                    // disabled={rules.length === 1}
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {section &&
          transactionRules
            .find((r) => r.id === ruleId)?.[section].groups?.map((group) => {
              return (
                <div
                  key={group.id}
                  className="ml-6 p-4 bg-purple-50 rounded-lg border border-purple-200 border-l-4 border-l-purple-500"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-purple-700 flex items-center gap-2">
                        <i className="ri-group-line"></i>
                        Group
                      </span>
                      <div className="flex items-center gap-2 bg-purple-100 rounded-lg p-1">
                        <button
                          type="button"
                          onClick={() =>
                            updateGroupCondition(ruleId, section, group.id, 'AND')
                          }
                          className={`px-2 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer ${group.condition === 'AND'
                            ? 'bg-white text-purple-900 shadow-sm'
                            : 'text-purple-600 hover:text-purple-900'
                            }`}
                        >
                          AND
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            updateGroupCondition(ruleId, section, group.id, 'OR')
                          }
                          className={`px-2 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer ${group.condition === 'OR'
                            ? 'bg-white text-purple-900 shadow-sm'
                            : 'text-purple-600 hover:text-purple-900'
                            }`}
                        >
                          OR
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => addRuleToGroup(ruleId, section, group.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors cursor-pointer text-xs font-medium"
                      >
                        <i className="ri-add-line text-xs"></i>
                        Rule
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeGroup(ruleId, section, group.id)}
                      className="w-6 h-6 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-xs"></i>
                    </button>
                  </div>

                  <div className="space-y-2">
                    {group.rules.map((rule, ruleIndex) => {
                      const currentField = fields.find((f) => f.name === rule.field);
                      const operators = getOperatorsForField(currentField?.fieldtype || currentField?.DataType, currentField?.valueType);
                      const currentOperator = operators.find(op => op.value === rule.operator);
                      const requiresSecondValue = currentOperator?.requiresSecondValue || false;

                      return (
                        <div
                          key={ruleIndex}
                          className={`p-2 rounded-md border ${requiresSecondValue ? 'bg-white border-purple-200' : 'bg-white border-purple-200'}`}
                        >
                          <div className={`grid gap-2 ${requiresSecondValue ? 'grid-cols-1 md:grid-cols-5' : 'grid-cols-1 md:grid-cols-4'}`}>
                            <div>
                              {error ? (
                                <div className="text-xs text-red-500">{error}</div>
                              ) : fields.length === 0 ? (
                                <div className="text-xs text-red-500">No fields available</div>
                              ) : (
                                <select
                                  value={rule.field}
                                  onChange={(e) =>
                                    updateGroupRule(
                                      ruleId,
                                      section,
                                      group.id,
                                      ruleIndex,
                                      'field',
                                      e.target.value
                                    )
                                  }
                                  className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-xs bg-white"
                                >
                                  <option value="">Select Field</option>
                                  {fields.map((field) => (
                                    <option key={field.name} value={field.name}>
                                      {field.label}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>

                            <div>
                              <select
                                value={rule.operator}
                                onChange={(e) =>
                                  updateGroupRule(
                                    ruleId,
                                    section,
                                    group.id,
                                    ruleIndex,
                                    'operator',
                                    e.target.value
                                  )
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-xs bg-white"
                              >
                                <option value="">Select Operator</option>
                                {operators.map((op) => (
                                  <option key={op.value} value={op.value}>
                                    {op.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <input
                                type={currentField?.valueType || 'text'}
                                value={rule.value}
                                onChange={(e) =>
                                  updateGroupRule(
                                    ruleId,
                                    section,
                                    group.id,
                                    ruleIndex,
                                    'value',
                                    e.target.value
                                  )
                                }
                                placeholder={requiresSecondValue ? "Start value" : "Enter value"}
                                className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-xs bg-white"
                              />
                            </div>

                            {requiresSecondValue && (
                              <div>
                                <input
                                  type={currentField?.valueType || 'text'}
                                  value={rule.value2 || ''}
                                  onChange={(e) =>
                                    updateGroupRule(
                                      ruleId,
                                      section,
                                      group.id,
                                      ruleIndex,
                                      'value2',
                                      e.target.value
                                    )
                                  }
                                  placeholder="End value"
                                  className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-xs bg-white"
                                />
                              </div>
                            )}

                            <div className="flex items-center justify-end">
                              <button
                                type="button"
                                onClick={() =>
                                  removeRuleFromGroup(ruleId, section, group.id, ruleIndex)
                                }
                                className="w-6 h-6 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors cursor-pointer"
                                // disabled={group.rules.length === 1}
                              >
                                <i className="ri-delete-bin-line text-xs"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
      </div>
    </div>
  );

  // Rest of the component remains the same...
  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50">
        {loading && <Loader />}
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Link
                to="/campaigns"
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-white rounded-xl transition-all duration-200 cursor-pointer shadow-sm border border-gray-200"
              >
                <i className="ri-arrow-left-line text-lg"></i>
              </Link>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Edit Campaign</h1>
                <p className="text-gray-600 mt-1">
                  Update your loyalty campaign to engage your customers
                </p>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Campaign Form */}
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Campaign Details Card - Same as before */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <i className="ri-information-line text-blue-600"></i>
                    Campaign Details
                  </h2>
                </div>

                <div className="p-6 space-y-6">
                  {/* Campaign Name, Start Date, End Date */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-1">
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Campaign Name
                      </label>
                      <input
                        type="text"
                        value={formData.rule_name}
                        onChange={(e) => handleInputChange('rule_name', e.target.value)}
                        placeholder="Enter campaign name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-gray-50"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => handleInputChange('start_date', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-gray-50"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => handleInputChange('end_date', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-gray-50"
                        required
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.rule_description}
                      onChange={(e) => handleInputChange('rule_description', e.target.value)}
                      placeholder="Describe your campaign objectives and benefits"
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-gray-50 resize-none"
                    />
                  </div>

                  {/* Status Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-1">
                        Campaign Status
                      </label>
                      <p className="text-sm text-gray-600">
                        Enable or disable this campaign
                      </p>
                    </div>
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => handleInputChange('status', formData.status === "1" ? "0" : "1")}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer ${formData.status === "1" ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 ${formData.status === "1" ? 'translate-x-6' : 'translate-x-1'
                            }`}
                        />
                      </button>
                      <span
                        className={`ml-3 text-sm font-medium ${formData.status === "1" ? 'text-blue-600' : 'text-gray-500'
                          }`}
                      >
                        {formData.status === "1" ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  {/* Campaign Image Upload */}
                 <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Campaign Image
                    </label>

                    <div className="max-w-md">
                      <div className="relative border-2 border-dashed border-gray-300 rounded-xl p-4 text-center">
                        {/* 🔥 LOADER (BOX KE ANDAR ONLY) */}
                        {imageUploading && (
                          <div className="absolute inset-0 bg-white/70 flex flex-col items-center justify-center rounded-xl z-20">
                            <i className="ri-loader-4-line text-3xl text-blue-600 animate-spin mb-2"></i>
                            <p className="text-xs text-gray-600">
                              Uploading...
                            </p>
                          </div>
                        )}

                        {formData.imagePreview ? (
                          <div className="relative">
                            <img
                              src={formData.imagePreview}
                              alt="Preview"
                              className="w-full h-32 object-cover rounded-xl"
                            />

                            <button
                              type="button"
                              onClick={removeImage}
                              className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center"
                            >
                              <i className="ri-close-line text-xs"></i>
                            </button>
                          </div>
                        ) : (
                          <>
                            <i className="ri-image-line text-2xl text-gray-400 mb-2"></i>
                            <p className="text-gray-600 text-sm mb-1">
                              Upload image
                            </p>
                            <p className="text-xs text-gray-500 mb-3">
                              PNG, JPG up to 5MB
                            </p>

                            <label className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-lg cursor-pointer text-sm">
                              <i className="ri-upload-line text-sm"></i>
                              Choose
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                className="hidden"
                                disabled={imageUploading}
                              />
                            </label>
                          </>
                        )}
                      </div>

                      {formData.campaignImageUrl && (
                        <p className="mt-2 text-sm text-green-600">
                        Uploaded: {formData.campaignImageUrl}
                        </p>
                      )}

                      {error && (
                        <p className="mt-2 text-sm text-red-600">{error}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Rules - Updated to use new render function */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <i className="ri-user-settings-line text-green-600"></i>
                    Customer Rules
                  </h2>
                </div>

          <div className="p-6">
  {renderRuleBuilder(
    null,
    customerRule.rules,
    customerRule.condition,
    addRule,
    removeRule,
    updateRule,
    (condition) =>
      setCustomerRule((prev) => ({
        ...prev,
        condition,
      })),
    null,
    customerFields
  )}
</div>
              </div>

              {/* Transaction Rules - Updated to use new render function */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <i className="ri-exchange-line text-orange-600"></i>
                      Transaction Rules
                    </h2>
                    <button
                      type="button"
                      onClick={addTransactionRule}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors cursor-pointer text-sm font-medium"
                    >
                      <i className="ri-add-line"></i>
                      Add Transaction Rule
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {error ? (
                    <div className="text-center py-12 text-red-500">
                      <i className="ri-error-warning-line text-4xl mb-4"></i>
                      <p className="text-lg font-medium">{error}</p>
                      <button
                        type="button"
                        onClick={fetchData}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer text-sm font-medium"
                      >
                        Retry
                      </button>
                    </div>
                  ) : transactionFields.length === 0 ? (
                    <div className="text-center py-12 text-red-500">
                      <i className="ri-error-warning-line text-4xl mb-4"></i>
                      <p className="text-lg font-medium">No transaction fields available</p>
                    </div>
                  ) : (
                    <div className="space-y-8">
                      {transactionRules.map((transactionRule, ruleIndex) => (
                        <div
                          key={transactionRule.id}
                          className="border border-gray-200 rounded-xl overflow-hidden"
                        >
                          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <input
                                  type="text"
                                  value={transactionRule.name}
                                  onChange={(e) =>
                                    updateTransactionRuleName(
                                      transactionRule.id,
                                      e.target.value
                                    )
                                  }
                                  className="text-md font-semibold bg-transparent border-none outline-none text-gray-900 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 py-1"
                                />
                                <span className="text-sm text-gray-500">
                                  #{ruleIndex + 1}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeTransactionRule(transactionRule.id)}
                                className="w-8 h-8 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                                disabled={transactionRules.length === 1}
                              >
                                <i className="ri-delete-bin-line text-sm"></i>
                              </button>
                            </div>
                          </div>

                          <div className="p-6 space-y-8">
                            {/* Incoming Rule */}
                            <div>
                              <h3 className="text-md font-semibold text-gray-900 mb-4">
                                Incoming Rule
                              </h3>
                              {renderRuleBuilder(
                                transactionRule.id,
                                transactionRule.incomingRule.rules,
                                transactionRule.incomingRule.condition,
                                () =>
                                  addTransactionRuleToSection(
                                    transactionRule.id,
                                    'incomingRule'
                                  ),
                                (index) =>
                                  removeTransactionRuleFromSection(
                                    transactionRule.id,
                                    'incomingRule',
                                    index
                                  ),
                                (index, field, value) =>
                                  updateTransactionRuleInSection(
                                    transactionRule.id,
                                    'incomingRule',
                                    index,
                                    field,
                                    value
                                  ),
                                (condition) =>
                                  updateTransactionRuleCondition(
                                    transactionRule.id,
                                    'incomingRule',
                                    condition
                                  ),
                                'incomingRule',
                                transactionFields
                              )}
                            </div>

                            {/* Reward Configuration */}
                            <div>
                              <h3 className="text-md font-semibold text-gray-900 mb-4">
                                Reward Configuration
                              </h3>
                              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <div>
                                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                                    Earning Type
                                  </label>
                                  <div className="relative">
                                    <select
                                      value={formData.earningtype}
                                      onChange={(e) => handleInputChange('earningtype', e.target.value)}
                                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-gray-50 appearance-none pr-10"
                                    >
                                      {earningTypes.map((type) => (
                                        <option key={type.value} value={type.value}>
                                          {type.label}
                                        </option>
                                      ))}
                                    </select>
                                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                                    Reward Ratio
                                  </label>
                                  <input
                                    type="number"
                                    value={formData.rewardratio}
                                    onChange={(e) => handleInputChange('rewardratio', e.target.value)}
                                    placeholder="Enter ratio value"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-gray-50"
                                    min="0"
                                    step="0.01"
                                  />
                                </div>

                                <div>
                                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                                    Earning Value
                                  </label>
                                  <input
                                    type="number"
                                    value={formData.earningvalue}
                                    onChange={(e) => handleInputChange('earningvalue', e.target.value)}
                                    placeholder="Enter earning value"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-gray-50"
                                    min="0"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Advanced Rules Button */}
                            <div className="flex items-center gap-4">
                              <button
                                type="button"
                                onClick={() =>
                                  setShowAdvancedRules((prev) => ({
                                    ...prev,
                                    [transactionRule.id]: !prev[transactionRule.id],
                                  }))
                                }
                                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors cursor-pointer text-sm font-medium"
                              >
                                <i
                                  className={`${showAdvancedRules[transactionRule.id]
                                    ? 'ri-arrow-up-s-line'
                                    : 'ri-arrow-down-s-line'
                                    }`}
                                ></i>
                                Advanced Rules
                              </button>
                            </div>

                            {/* Advanced Rules Section */}
                            {showAdvancedRules[transactionRule.id] && (
                              <div className="space-y-8 pt-4 border-t border-gray-200 bg-gray-50/50 rounded-xl p-6 -mx-6">
                                <div className="bg-white rounded-lg p-6 shadow-sm">
                                  {/* Rule on Function Existing */}
                                  <div className="mb-8">
                                    <h3 className="text-md font-semibold text-gray-900 mb-4">
                                      Rule on Function Existing
                                    </h3>
                                    {renderRuleBuilder(
                                      transactionRule.id,
                                      transactionRule.functionExistingRule.rules,
                                      transactionRule.functionExistingRule.condition,
                                      () =>
                                        addTransactionRuleToSection(
                                          transactionRule.id,
                                          'functionExistingRule'
                                        ),
                                      (index) =>
                                        removeTransactionRuleFromSection(
                                          transactionRule.id,
                                          'functionExistingRule',
                                          index
                                        ),
                                      (index, field, value) =>
                                        updateTransactionRuleInSection(
                                          transactionRule.id,
                                          'functionExistingRule',
                                          index,
                                          field,
                                          value
                                        ),
                                      (condition) =>
                                        updateTransactionRuleCondition(
                                          transactionRule.id,
                                          'functionExistingRule',
                                          condition
                                        ),
                                      'functionExistingRule',
                                      transactionFields
                                    )}
                                  </div>

                                  {/* Function on Existing Transactions */}
                                  <div className="mb-8">
                                    <h3 className="text-md font-semibold text-gray-900 mb-4">
                                      Function on Existing Transactions
                                    </h3>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                      <div>
                                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                                          Duration
                                        </label>
                                        <div className="relative">
                                          <select
                                            value={transactionRule.functionDuration}
                                            onChange={(e) =>
                                              setTransactionRules((prev) =>
                                                prev.map((rule) =>
                                                  rule.id === transactionRule.id
                                                    ? {
                                                      ...rule,
                                                      functionDuration: e.target.value,
                                                    }
                                                    : rule
                                                )
                                              )
                                            }
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-gray-50 appearance-none pr-10"
                                          >
                                            <option value="">Select</option>
                                            {durationOptions.map((option) => (
                                              <option
                                                key={option.value}
                                                value={option.value}
                                              >
                                                {option.label}
                                              </option>
                                            ))}
                                          </select>
                                          <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                                        </div>
                                      </div>

                                      {transactionRule.functionDuration === 'Custom' ? (
                                        <>
                                          <div>
                                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                                              Start Date
                                            </label>
                                            <input
                                              type="date"
                                              value={transactionRule.frequencyStartDate}
                                              onChange={(e) =>
                                                setTransactionRules((prev) =>
                                                  prev.map((rule) =>
                                                    rule.id === transactionRule.id
                                                      ? {
                                                        ...rule,
                                                        frequencyStartDate: e.target.value,
                                                      }
                                                      : rule
                                                  )
                                                )
                                              }
                                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-gray-50"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                                              End Date
                                            </label>
                                            <input
                                              type="date"
                                              value={transactionRule.frequencyEndDate}
                                              onChange={(e) =>
                                                setTransactionRules((prev) =>
                                                  prev.map((rule) =>
                                                    rule.id === transactionRule.id
                                                      ? {
                                                        ...rule,
                                                        frequencyEndDate: e.target.value,
                                                      }
                                                      : rule
                                                  )
                                                )
                                              }
                                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-gray-50"
                                            />
                                          </div>
                                        </>
                                      ) : (
                                        <div>
                                          <label className="block text-sm font-semibold text-gray-900 mb-2">
                                            Value
                                          </label>
                                          <input
                                            type="number"
                                            value={transactionRule.functionValue}
                                            onChange={(e) =>
                                              setTransactionRules((prev) =>
                                                prev.map((rule) =>
                                                  rule.id === transactionRule.id
                                                    ? {
                                                      ...rule,
                                                      functionValue: e.target.value,
                                                    }
                                                      : rule
                                                )
                                              )
                                            }
                                            placeholder="0"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-gray-50"
                                            min="0"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Additional Rules */}
                                  <div>
                                    <h3 className="text-md font-semibold text-gray-900 mb-4">
                                      Additional Rules
                                    </h3>
                                    {renderRuleBuilder(
                                      transactionRule.id,
                                      transactionRule.functionAdditionalRule.rules,
                                      transactionRule.functionAdditionalRule.condition,
                                      () =>
                                        addTransactionRuleToSection(
                                          transactionRule.id,
                                          'functionAdditionalRule'
                                        ),
                                      (index) =>
                                        removeTransactionRuleFromSection(
                                          transactionRule.id,
                                          'functionAdditionalRule',
                                          index
                                        ),
                                      (index, field, value) =>
                                        updateTransactionRuleInSection(
                                          transactionRule.id,
                                          'functionAdditionalRule',
                                          index,
                                          field,
                                          value
                                        ),
                                      (condition) =>
                                        updateTransactionRuleCondition(
                                          transactionRule.id,
                                          'functionAdditionalRule',
                                          condition
                                        ),
                                      'functionAdditionalRule',
                                      functionTransactionFields
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between gap-4 pt-6 pb-12">
                <Link
                  to="/campaigns"
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer font-medium"
                >
                  Cancel
                </Link>

                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer font-medium"
                  >
                    Save
                  </button>

                  <button
                    type="submit"
                    disabled={loading}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:bg-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 font-semibold shadow-lg cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {loading ? 'Updating...' : 'Update Campaign'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Campaign Updated Successfully
                </h2>
                <p className="text-gray-600 mb-6">
                  Your campaign "{formData.rule_name}" has been updated and is
                  ready to engage your customers.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowSuccessModal(false);
                      fetchData();
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    Continue Editing
                  </button>
                  <button
                    onClick={handleSuccessClose}
                    className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-colors cursor-pointer"
                  >
                    View Campaigns
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image Upload Success Modal */}
        {showImageSuccessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Image Uploaded Successfully
                </h2>
                <p className="text-gray-600 mb-6 break-words">
                  Image URL: {formData.campaignImageUrl}
                </p>
                <button
                  onClick={handleImageSuccessClose}
                  className="px-6 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-colors cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}